<template>
  <div class="tours-container">
    <div class="tours-hero" :style="{ backgroundImage: `url(${cityData.bannerImage})` }">
      <div class="tours-hero-content">
        <div class="content-wrapper">
          <div class="breadcrumb">
            <router-link to="/destination">Destinations</router-link>
          </div>
          <h1>{{ cityData.name }}</h1>
          <p>{{ cityData.brief }}</p>
        </div>
      </div>
    </div>

    <div class="tours-content">
      <div class="tours-main">
        <div class="city-detail">
          <div class="tours-header">
            <h2><i><b><u>About {{ cityData.name }}</u></b></i></h2>
            <router-link to="/destination" class="back-btn">
              <font-awesome-icon icon="arrow-left" /> Back to All Cities
            </router-link>
          </div>
          
          <div class="city-intro">
            <div class="city-description">
              <p>{{ cityData.description }}</p>
            </div>
            <div class="city-map">
              <img :src="cityData.mapImage" :alt="`Map of ${cityData.name}`">
            </div>
          </div>
          
          <section id="attractions" class="city-section">
            <h3 class="decorated-title"><i><b><u>What to See in {{ cityData.name }}</u></b></i></h3>
            <p>{{ cityData.attractions.description }}</p>
            <div class="attractions-grid">
              <div v-for="(attraction, index) in cityData.attractions.places" :key="index" class="attraction-card">
                <img :src="attraction.image" :alt="attraction.name">
                <div class="attraction-info">
                  <h4>{{ attraction.name }}</h4>
                </div>
              </div>
            </div>
          </section>
          
          <section id="minorities" class="city-section">
            <h3 class="decorated-title"><i><b><u>Minorities in {{ cityData.name }}</u></b></i></h3>
            <p>{{ cityData.minorities.description }}</p>
            <div class="minorities-grid">
              <div v-for="(minority, index) in cityData.minorities.groups" :key="index" class="minority-card">
                <img :src="minority.image" :alt="minority.name">
                <div class="minority-info">
                  <h4>{{ minority.name }}</h4>
                </div>
              </div>
            </div>
          </section>
          
          <section id="food" class="city-section">
            <h3 class="decorated-title"><i><b><u>What to Eat in {{ cityData.name }}</u></b></i></h3>
            <p>{{ cityData.food.description }}</p>
            <div class="food-grid">
              <div v-for="(dish, index) in cityData.food.dishes" :key="index" class="food-card">
                <img :src="dish.image" :alt="dish.name">
                <div class="food-info">
                  <h4>{{ dish.name }}</h4>
                </div>
              </div>
            </div>
          </section>
          
          <section id="related-tours" class="city-section">
            <h3 class="decorated-title"><i><b><u>Related {{ cityData.name }} Tours</u></b></i></h3>
            <p>{{ cityData.tours.description }}</p>
            <div v-if="cityTours.length === 0" class="no-results">
              <p>No tours currently available for this city. Please check back later.</p>
            </div>
            
            <div v-else class="tours-grid">
              <div v-for="tour in cityTours" :key="tour.id" class="tour-card">
                <router-link :to="`/tours/${tour.category}/${tour.id}`" >
                  <div class="tour-image">
                    <img :src="tour.image" :alt="tour.title">
                  </div>
                  <div class="tour-content">
                    <h3 class="tour-title">{{ tour.title }}</h3>
                    <p class="tour-subtitle">{{ tour.subtitle }}</p>
                  </div>
              </router-link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { cities } from '../data/cityData'
import { tourData } from '../data/tourData'

const route = useRoute()

// Get city data from route parameter
const cityData = computed(() => {
  const cityId = route.params.city
  return cities.find(city => city.id === cityId) || cities[0]
})

// Get tours for selected city
const cityTours = computed(() => {
  const cityId = route.params.city
  const city = cities.find(city => city.id === cityId)
  
  if (!city || !city.relatedTours) {
    return []
  }
  
  // Get the related tours from tourData
  return city.relatedTours.map(tourId => {
    // Search through all tour categories to find the matching tour
    for (const category in tourData) {
      const tour = tourData[category].find(t => t.id === tourId)
      if (tour) {
        return {
          ...tour,
          category
        }
      }
    }
    return null
  }).filter(tour => tour !== null)
})

</script>

<style scoped>
.tours-container {
  width: 100%;
  min-height: calc(100vh - 70px);
  background-color: var(--color-background-soft);
}

.tours-hero {
  width: 100%;
  min-height: 450px;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  color: white;
}

.tours-hero-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
}

.content-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  background-color: rgb(0 0 0 / 27%);
  border-radius: 8px;
  padding: 1.5rem;
}

.breadcrumb {
  display: inline-block;
  font-size: 1rem;
  padding: 0;
}

.breadcrumb a {
  color: white;
  text-decoration: none;
  opacity: 0.8;
  transition: opacity 0.3s;
}

.breadcrumb a:hover {
  opacity: 1;
}

.tours-hero h1 {
  font-size: 2.5rem;
  display: inline-block;
  padding: 0;
  margin: 0.5rem 0;
}

.tours-hero p {
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 800px;
  display: inline-block;
  padding: 0;
  margin: 0;
}

.tours-content {
  width: 100%;
  margin: 0 auto;
}

.tours-main {
  background: var(--color-background);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  padding: 2rem 0;
}

.tours-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 50px;
}

.tours-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--color-heading);
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--color-background-soft);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: var(--color-text);
  transition: all 0.2s ease;
  text-decoration: none;
}

.back-btn:hover {
  background: var(--color-border);
}

/* City Detail */
.city-detail {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.city-intro {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 50px;
}

.city-description {
  font-size: 1.2rem;
  line-height: 1.8;
  color: var(--color-text);
}

.city-map {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.city-map img {
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}

.city-section {
  margin-bottom: 3rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 2rem;
}

.city-section h3.decorated-title {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: var(--color-heading);
  position: relative;
  padding-bottom: 15px;
  text-align: left;
  margin-left: 50px;
}

.city-section h3.decorated-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 60px;
  height: 4px;
  background-color: #008c5c;
}

.city-section p {
  margin-bottom: 2rem;
  line-height: 1.8;
  color: var(--color-text);
  font-size: 1.2rem;
  max-width: 900px;
  margin-left: 50px;
}

.attractions-grid,
.minorities-grid,
.food-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 50px;
}

.attraction-card, .minority-card, .food-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.attraction-card:hover, .minority-card:hover, .food-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
}

.attraction-card img, .minority-card img, .food-card img {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
}

.attraction-info, .minority-info, .food-info {
  padding: 1rem;
  background: var(--color-background);
  width: 100%;
  text-align: center;
}

.attraction-info h4, .minority-info h4, .food-info h4 {
  margin: 0;
  color: var(--color-heading);
  font-size: 1.1rem;
}

/* Tours List View */
.tours-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 50px;
}

.tour-card {
  background: var(--color-background);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tour-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
  background-color: transparent;
}

.tour-image {
  width: 100%;
  height: 180px;
  overflow: hidden;
}

.tour-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.tour-card:hover .tour-image img {
  transform: scale(1.05);
}

.tour-content {
  padding: 1.2rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  text-align: center;
}

.tour-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-heading);
  line-height: 1.3;
  text-align: center;
}

.city-section .tour-subtitle {
  margin: 0 0 0.6rem;
  font-size: 0.8rem;
  color: white;
  line-height: 1.3;
  text-align: center;
  width: 100%;
  background-color: #524246;
  padding: 0.3rem 0.3rem;
  border-radius: 1.5rem;
}

.tour-duration {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0.3rem auto 0.6rem;
  color: #666;
  font-size: 0.85rem;
  width: fit-content;
  padding: 0.3rem 0.8rem;
  background-color: rgba(0, 140, 92, 0.1);
  border-radius: 4px;
}

.view-details-btn {
  display: inline-block;
  margin-top: 0.6rem;
  padding: 0.5rem 1.2rem;
  background-color: #008c5c;
  color: white;
  text-align: center;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 140, 92, 0.2);
  min-width: 120px;
}

.view-details-btn:hover {
  background-color: #00734b;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 140, 92, 0.3);
}

.no-results {
  background: var(--color-background-soft);
  padding: 2rem;
  text-align: center;
  border-radius: 8px;
  color: var(--color-text);
}

@media (max-width: 1200px) {
  .tours-grid {
    grid-template-columns: repeat(2, 1fr);
    padding: 0 25px;
  }
}

@media (max-width: 768px) {
  .tours-hero {
    min-height: 300px;
  }

  .tours-hero h1 {
    font-size: 1.8rem;
    margin: 0.4rem 0;
  }

  .tours-hero p {
    font-size: 1rem;
  }
  
  .back-btn {
    display: none;
  }

  .content-wrapper {
    padding: 1.2rem;
  }
  
  .city-detail {
    padding: 0 1rem;
  }

  .city-intro {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0;
    margin-bottom: 2rem;
  }

  .city-description {
    font-size: 1rem;
    line-height: 1.7;
  }

  .city-map img {
    max-width: 100%;
  }

  .tours-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
    margin-bottom: 1.5rem;
  }

  .city-section {
    padding: 0;
    margin-bottom: 2rem;
  }

  .city-section h3.decorated-title {
    font-size: 1.6rem;
    margin-left: 0;
    padding-bottom: 10px;
  }

  .city-section h3.decorated-title::after {
    width: 50px;
    height: 3px;
    bottom: 0;
  }

  .city-section p {
    font-size: 1rem;
    margin-left: 0;
    margin-bottom: 1.5rem;
    line-height: 1.7;
  }

  .attractions-grid,
  .minorities-grid,
  .food-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    padding: 0;
  }

  .tours-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0;
  }

  .attraction-card img, .minority-card img, .food-card img {
    height: 160px;
  }

  .attraction-info, .minority-info, .food-info {
    padding: 0.8rem;
    min-height: 50px;
  }

  .attraction-info h4, .minority-info h4, .food-info h4 {
    font-size: 1rem;
  }

  .tour-image {
    height: 200px;
  }

  .tour-content {
    padding: 1rem;
  }

  .tour-title {
    font-size: 1.1rem;
  }

  .view-details-btn {
    padding: 0.4rem 1rem;
  }
}

@media (max-width: 480px) {
  .tours-hero {
    min-height: 250px;
  }
  .tours-hero-content {
    padding: 2rem 1.5rem;
  }
  
  .content-wrapper {
    padding: 1rem;
  }
  
  .breadcrumb {
    font-size: 0.9rem;
  }

  .city-detail {
    padding: 0 0.8rem;
  }

  .city-intro {
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  .city-description {
    font-size: 0.95rem;
  }

  .city-section h3.decorated-title {
    font-size: 1.4rem;
    padding-bottom: 8px;
  }
  .city-section h3.decorated-title::after {
    width: 40px;
    height: 2px;
  }

  .city-section p {
    font-size: 0.95rem;
    margin-bottom: 1rem;
  }

  .attractions-grid,
  .minorities-grid,
  .food-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .attraction-card img, .minority-card img, .food-card img {
    height: 180px;
  }
  .attraction-info, .minority-info, .food-info {
    padding: 0.8rem;
    min-height: auto;
  }
  .attraction-info h4, .minority-info h4, .food-info h4 {
    font-size: 1rem;
  }

  .tours-grid {
    gap: 1rem;
  }
  .tour-image {
    height: 180px;
  }
  .view-details-btn {
    padding: 0.4rem 1rem;
  }
  .tour-title {
    font-size: 1rem;
  }
}

</style> 
